/**
 * Goodbye Letter Settings — fetch helper shared by browser + edge.
 *
 * Browser callers go through src/services/goodbyeLetterSettingsService.ts,
 * which supplies the browser supabase client. Edge callers (the
 * send-goodbye-letter function) supply the service-role client. The function
 * here is the single source of truth for the row→camelCase mapping.
 */

import type {
  GoodbyeLetterSettings,
  GoodbyeLetterSettingsRow
} from './goodbye-letter-types.ts';

// Structural-only typing — accept any Supabase-shaped client so both
// `@supabase/supabase-js` browser instances and Deno edge service-role clients
// fit without an import dependency on either side.
// deno-lint-ignore no-explicit-any
type SupabaseLike = any;

export function rowToGoodbyeLetterSettings(
  row: GoodbyeLetterSettingsRow
): GoodbyeLetterSettings {
  return {
    id: row.id,
    organizationId: row.organization_id,
    templateId: row.template_id,
    paymentContactRole: row.payment_contact_role,
    customerServiceContactRole: row.customer_service_contact_role,
    correspondenceContactRole: row.correspondence_contact_role,
    customerServicePhoneOverride: row.customer_service_phone_override,
    servicerNameOverride: row.servicer_name_override,
    correspondenceAttention: row.correspondence_attention,
    qwrAttention: row.qwr_attention,
    onlinePaymentsEnabled: row.online_payments_enabled,
    onlinePaymentsUrl: row.online_payments_url,
    payByPhoneEnabled: row.pay_by_phone_enabled,
    payByPhoneNumber: row.pay_by_phone_number,
    autoDeductionEnabled: row.auto_deduction_enabled,
    autoDeductionInstructions: row.auto_deduction_instructions,
    insuranceImpactStatement: row.insurance_impact_statement,
    paServicerCodes: row.pa_servicer_codes ?? [],
    propertyStates: row.property_states ?? [],
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

export async function fetchGoodbyeLetterSettings(
  client: SupabaseLike,
  templateId: string
): Promise<GoodbyeLetterSettings | null> {
  const { data, error } = await client
    .from('investor_encompass_template_goodbye_settings')
    .select('*')
    .eq('template_id', templateId)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch goodbye-letter settings', { templateId, error });
    throw error;
  }
  return data ? rowToGoodbyeLetterSettings(data as GoodbyeLetterSettingsRow) : null;
}
